import type {
  TutorialAnchor,
  TutorialStep,
} from "@v2/features/activation/constants/activationContent";

import * as React from "react";

import { useTutorialProgress } from "@v2/features/activation/hooks/useTutorialProgress";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { useSelectInitialProjectSetupModal } from "@v2/features/onboarding/useSelectInitialProjectSetupModal";
import { routes } from "@v2/utils/router";
import { matchPath, useLocation } from "react-router-dom";

import { TutorialOverlay, TutorialPopover } from "./TutorialPopover";

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface HomepageTutorialContextValue {
  isActive: boolean;
  currentStep: TutorialStep | null;
  /** 1-indexed */
  currentStepNumber: number;
  totalSteps: number;
  activeAnchor: TutorialAnchor;
  onNext: () => void;
  onSkip: () => void;
  isLastStep: boolean;
  setAnchorRect: (rect: AnchorRect | null) => void;
  anchorRect: AnchorRect | null;
}

const HomepageTutorialContext =
  React.createContext<HomepageTutorialContextValue | null>(null);

function useHomepageTutorial() {
  return React.useContext(HomepageTutorialContext);
}

type HomepageTutorialProviderProps = React.PropsWithChildren;

export function HomepageTutorialProvider({
  children,
}: HomepageTutorialProviderProps) {
  const isEnabled = isFeatureEnabled("user-activation-tutorial");
  const { modalToShow } = useSelectInitialProjectSetupModal();
  const location = useLocation();

  // Only show tutorial on the actual homepage route (/shops/:projectId/home)
  const isOnHomepage = Boolean(matchPath(routes.shops.home, location.pathname));

  const {
    shouldShowTutorial,
    currentStep,
    currentStepIndex,
    totalSteps,
    advanceToNextStep,
    skipTutorial,
    isLoading,
  } = useTutorialProgress({
    enabled: isEnabled,
    tutorialLocation: "homepage",
  });

  const [anchorRect, setAnchorRect] = React.useState<AnchorRect | null>(null);

  const activeAnchor: TutorialAnchor = React.useMemo(() => {
    if (!shouldShowTutorial || !currentStep) {
      return null;
    }
    return currentStep.tutorialAnchorId ?? null;
  }, [shouldShowTutorial, currentStep]);

  // Only show tutorial when there's no setup modal open
  const noModalShowing = modalToShow === null;

  const contextValue = React.useMemo<HomepageTutorialContextValue>(
    () => ({
      isActive:
        isEnabled &&
        shouldShowTutorial &&
        !isLoading &&
        noModalShowing &&
        isOnHomepage,
      currentStep,
      currentStepNumber: currentStepIndex + 1,
      totalSteps,
      activeAnchor,
      onNext: advanceToNextStep,
      onSkip: skipTutorial,
      isLastStep: currentStepIndex === totalSteps - 1,
      setAnchorRect,
      anchorRect,
    }),
    [
      isEnabled,
      shouldShowTutorial,
      isLoading,
      noModalShowing,
      isOnHomepage,
      currentStep,
      currentStepIndex,
      totalSteps,
      activeAnchor,
      advanceToNextStep,
      skipTutorial,
      anchorRect,
    ],
  );

  return (
    <HomepageTutorialContext.Provider value={contextValue}>
      <TutorialOverlay
        isVisible={contextValue.isActive}
        anchorRect={anchorRect}
      />
      {children}
    </HomepageTutorialContext.Provider>
  );
}

type TutorialAnchorWrapperProps = React.PropsWithChildren<{
  /** The anchor ID this wrapper represents */
  anchorId: TutorialAnchor;
  /** Popover position */
  side?: "top" | "bottom" | "left" | "right";
  /** Offset from anchor */
  sideOffset?: number;
  /** Alignment relative to anchor */
  align?: "start" | "center" | "end";
  /** Whether to show the step indicator (e.g., "1 of 3") */
  showStepIndicator?: boolean;
}>;

export function TutorialAnchorWrapper({
  anchorId,
  children,
  side = "right",
  sideOffset = 16,
  align = "start",
  showStepIndicator = true,
}: TutorialAnchorWrapperProps) {
  const tutorial = useHomepageTutorial();
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const isActiveAnchor =
    tutorial?.isActive && tutorial.activeAnchor === anchorId;

  // Extract setAnchorRect to avoid infinite re-render loop
  // (tutorial object changes when anchorRect changes, but setAnchorRect is stable)
  const setAnchorRect = tutorial?.setAnchorRect;

  // Track anchor position for overlay cutout
  React.useEffect(() => {
    if (!isActiveAnchor || !anchorRef.current || !setAnchorRect) {
      return;
    }

    const updateRect = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setAnchorRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateRect();

    // Update on scroll/resize
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);

    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      setAnchorRect(null);
    };
  }, [isActiveAnchor, setAnchorRect]);

  if (!tutorial || !isActiveAnchor) {
    return <>{children}</>;
  }

  if (!tutorial.currentStep) {
    return <>{children}</>;
  }

  return (
    <TutorialPopover
      step={tutorial.currentStep}
      currentStep={tutorial.currentStepNumber}
      totalSteps={tutorial.totalSteps}
      isOpen={true}
      onNext={tutorial.onNext}
      onSkip={tutorial.onSkip}
      side={side}
      sideOffset={sideOffset}
      align={align}
      isLastStep={tutorial.isLastStep}
      anchorRef={anchorRef}
      showStepIndicator={showStepIndicator}
    >
      {children}
    </TutorialPopover>
  );
}
